import React, { useCallback, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import { resolveArcaAssetUrl } from '../utils/arcaApi';

/**
 * Camada de mídia freeform: arrastar + redimensionar (canto SE).
 * Coordenadas em % do slide (x, y, w).
 */
export default function SlideMediaLayer({ midias = [], onChange, readOnly = false }) {
  const layerRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const dragRef = useRef(null);

  const patchMidia = useCallback(
    (id, patch) => {
      onChange(midias.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    },
    [midias, onChange]
  );

  const removeMidia = useCallback(
    (id) => {
      onChange(midias.filter((m) => m.id !== id));
      if (selectedId === id) setSelectedId(null);
    },
    [midias, onChange, selectedId]
  );

  const bringToFront = useCallback(
    (id) => {
      const maxZ = midias.reduce((acc, m) => Math.max(acc, Number(m.zIndex) || 1), 1);
      patchMidia(id, { zIndex: maxZ + 1 });
      setSelectedId(id);
    },
    [midias, patchMidia]
  );

  const onPointerDownMove = (e, m) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const layer = layerRef.current;
    if (!layer) return;
    bringToFront(m.id);
    const rect = layer.getBoundingClientRect();
    dragRef.current = {
      mode: 'move',
      id: m.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: Number(m.x) || 8,
      origY: Number(m.y) || 28,
      w: Number(m.w || m.largura) || 32,
      rectW: rect.width,
      rectH: rect.height,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerDownResize = (e, m) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const layer = layerRef.current;
    if (!layer) return;
    bringToFront(m.id);
    const rect = layer.getBoundingClientRect();
    dragRef.current = {
      mode: 'resize',
      id: m.id,
      startX: e.clientX,
      startY: e.clientY,
      origW: Number(m.w || m.largura) || 32,
      origX: Number(m.x) || 8,
      origY: Number(m.y) || 28,
      rectW: rect.width,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dxPct = ((e.clientX - d.startX) / d.rectW) * 100;
    if (d.mode === 'move') {
      const dyPct = ((e.clientY - d.startY) / d.rectH) * 100;
      const x = Math.min(95 - d.w, Math.max(0, d.origX + dxPct));
      const y = Math.min(90, Math.max(0, d.origY + dyPct));
      patchMidia(d.id, { x, y, largura: d.w, w: d.w });
    } else if (d.mode === 'resize') {
      const w = Math.min(90 - d.origX, Math.max(12, d.origW + dxPct));
      patchMidia(d.id, { w, largura: w });
    }
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  if (!midias.length) return null;

  return (
    <Box
      ref={layerRef}
      className="arca-media-layer"
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onClick={() => setSelectedId(null)}
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: readOnly ? 'none' : 'auto',
        zIndex: 5,
      }}
    >
      {midias.map((m, index) => {
        const src = resolveArcaAssetUrl(m.url || m.src);
        const w = Number(m.w || m.largura) || 32;
        const x = m.x != null ? Number(m.x) : 8 + (index % 3) * 28;
        const y = m.y != null ? Number(m.y) : 28 + Math.floor(index / 3) * 8;
        const selected = selectedId === m.id;

        return (
          <Box
            key={m.id}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(m.id);
            }}
            onPointerDown={(e) => onPointerDownMove(e, m)}
            sx={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y}%`,
              width: `${w}%`,
              zIndex: Number(m.zIndex) || index + 1,
              cursor: readOnly ? 'default' : 'grab',
              userSelect: 'none',
              touchAction: 'none',
              outline: selected ? '2px solid #38bdf8' : 'none',
              outlineOffset: 2,
              borderRadius: 1,
              boxShadow: selected ? '0 0 0 4px rgba(56,189,248,0.2)' : 'none',
            }}
          >
            <Box
              component="img"
              src={src}
              alt={m.nome || 'Imagem'}
              draggable={false}
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                maxHeight: 220,
                objectFit: 'contain',
                borderRadius: 1,
                pointerEvents: 'none',
                bgcolor: 'rgba(15,23,42,0.25)',
              }}
            />

            {!readOnly && selected && (
              <>
                <Box
                  className="arca-export-hide"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: 'rgba(15,23,42,0.75)',
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                  }}
                >
                  <OpenWithIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                  <Typography variant="caption" sx={{ color: '#cbd5e1', fontSize: 10 }}>
                    {Math.round(w)}%
                  </Typography>
                </Box>
                <Tooltip title="Remover">
                  <IconButton
                    className="arca-export-hide"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMidia(m.id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: '#0f172a',
                      color: '#f87171',
                      '&:hover': { bgcolor: '#1e293b' },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box
                  className="arca-export-hide"
                  onPointerDown={(e) => onPointerDownResize(e, m)}
                  sx={{
                    position: 'absolute',
                    right: -6,
                    bottom: -6,
                    width: 14,
                    height: 14,
                    bgcolor: '#38bdf8',
                    borderRadius: '2px',
                    cursor: 'nwse-resize',
                    border: '2px solid #0f172a',
                  }}
                />
              </>
            )}

            {m.licenca?.author && (
              <Typography
                className="arca-export-hide"
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.25,
                  color: 'rgba(148,163,184,0.85)',
                  fontSize: 9,
                  lineHeight: 1.2,
                }}
                noWrap
              >
                {m.fonte === 'unsplash' ? `Unsplash · ${m.licenca.author}` : m.nome}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
